import { ApiError } from '../identity/types';
import { getMockCurrentUser, MOCK_ADMIN_USER_ID, MOCK_APPROVER_USER_ID, type MockCurrentUser } from '../identity/services/mockCurrentUser';
import { PayrollRunStatus, type PayrollReviewRun, type PayrollRunCalculation } from './types';

/**
 * EPN-104 — MOCK MODE ONLY. The single shared in-memory store of Module 7 payroll runs, read by
 * `MockPayrollReviewGateway` and written by `MockCustomPayRunGateway.submitForReview` (so a run submitted from
 * Custom Pay Run in mock mode genuinely appears in Payroll Review, and its status there is what Custom Pay Run
 * displays). It reproduces the REAL `PayrollRunService` rules and exact error messages — pending-state checks,
 * the three approval segregation-of-duties rules, reject-reason required, terminal Approved/Rejected — so the
 * UI is exercised against the same refusals the real backend produces. It is not a source of truth for
 * anything: the production build (`VITE_USE_MOCKS=false`) never uses it.
 *
 * Deterministic seed fixtures (stable ids/dates) cover every UI state; `resetMockPayrollReviewStoreForTests()`
 * restores exactly the seed so no test can leak runs into another. Identity comes from the shared
 * `mockCurrentUser` holder, never from anything the caller passes in.
 */
export const MOCK_REVIEW_COMPANY_ID = '11111111-1111-1111-1111-111111111111';

/** A fixture-only preparer: never loggable-in, so seed runs can be "someone else's" for BOTH real mock users. */
const PREPARER = { userId: '99999999-9999-9999-9999-9999999999a1', email: 'preparer@epn.local' };
const ADMIN = { userId: MOCK_ADMIN_USER_ID, email: 'admin@epn.local' };
const APPROVER = { userId: MOCK_APPROVER_USER_ID, email: 'approver@epn.local' };

export const MOCK_REVIEW_RUN_IDS = {
  /** Pending; prepared and submitted by the fixture preparer — approvable by either mock user. */
  pendingOther: 'mock-review-pending-other',
  /** Pending; created AND submitted by admin@epn.local — admin cannot approve it, approver@epn.local can. */
  pendingOwnByAdmin: 'mock-review-pending-own-by-admin',
  /** Pending; prepared by the preparer, but a calculation in it was run by admin@epn.local — the server-only
   * third segregation rule: the UI cannot pre-check it, so admin's Approve is refused with a 400. */
  pendingCalcRunByAdmin: 'mock-review-pending-calc-run-by-admin',
  approved: 'mock-review-approved',
  rejected: 'mock-review-rejected',
} as const;

interface Entry {
  run: PayrollReviewRun;
  /** Mirrors `PayrollRunCalculation.CalculationCreatedByUserId` — not exposed by the real API response either. */
  calculationCreatedByUserIds: string[];
}

type Actor = { userId: string; email: string };

function seedRun(
  id: string, period: [string, string, string], status: string,
  created: Actor, submitted: Actor | null, createdAtUtc: string,
  extra: Partial<PayrollReviewRun> = {},
): PayrollReviewRun {
  return {
    id, companyId: MOCK_REVIEW_COMPANY_ID, payScheduleId: null,
    periodStartDate: period[0], periodEndDate: period[1], checkDate: period[2], status,
    createdByUserId: created.userId, createdByEmailSnapshot: created.email, createdAtUtc,
    submittedByUserId: submitted?.userId ?? null, submittedByEmailSnapshot: submitted?.email ?? null,
    submittedForApprovalAtUtc: submitted ? createdAtUtc : null,
    approvedByUserId: null, approvedByEmailSnapshot: null, approvedAtUtc: null,
    rejectedByUserId: null, rejectedByEmailSnapshot: null, rejectedAtUtc: null, rejectionReason: null,
    ...extra,
  };
}

function seed(): Map<string, Entry> {
  const entries: Entry[] = [
    {
      run: seedRun(MOCK_REVIEW_RUN_IDS.pendingOther, ['2026-03-15', '2026-03-28', '2026-04-03'], PayrollRunStatus.PendingApproval,
        PREPARER, PREPARER, '2026-03-30T15:00:00Z'),
      calculationCreatedByUserIds: [PREPARER.userId],
    },
    {
      run: seedRun(MOCK_REVIEW_RUN_IDS.pendingOwnByAdmin, ['2026-03-01', '2026-03-14', '2026-03-20'], PayrollRunStatus.PendingApproval,
        ADMIN, ADMIN, '2026-03-16T15:00:00Z'),
      calculationCreatedByUserIds: [ADMIN.userId],
    },
    {
      run: seedRun(MOCK_REVIEW_RUN_IDS.pendingCalcRunByAdmin, ['2026-02-15', '2026-02-28', '2026-03-06'], PayrollRunStatus.PendingApproval,
        PREPARER, PREPARER, '2026-03-02T15:00:00Z'),
      calculationCreatedByUserIds: [PREPARER.userId, ADMIN.userId],
    },
    {
      run: seedRun(MOCK_REVIEW_RUN_IDS.approved, ['2026-02-01', '2026-02-14', '2026-02-20'], PayrollRunStatus.Approved,
        PREPARER, PREPARER, '2026-02-16T15:00:00Z', {
          approvedByUserId: APPROVER.userId, approvedByEmailSnapshot: APPROVER.email, approvedAtUtc: '2026-02-17T15:00:00Z',
        }),
      calculationCreatedByUserIds: [PREPARER.userId],
    },
    {
      run: seedRun(MOCK_REVIEW_RUN_IDS.rejected, ['2026-01-15', '2026-01-31', '2026-02-06'], PayrollRunStatus.Rejected,
        PREPARER, PREPARER, '2026-02-02T15:00:00Z', {
          rejectedByUserId: APPROVER.userId, rejectedByEmailSnapshot: APPROVER.email, rejectedAtUtc: '2026-02-03T15:00:00Z',
          rejectionReason: 'Overtime hours for two employees look incorrect — recalculate before resubmitting.',
        }),
      calculationCreatedByUserIds: [PREPARER.userId],
    },
  ];
  return new Map(entries.map((e) => [e.run.id, e]));
}

let entries: Map<string, Entry> = seed();

/** TEST-ONLY (also safe in mock dev): restores exactly the seed fixtures, discarding every submitted/decided run. */
export function resetMockPayrollReviewStoreForTests(): void {
  entries = seed();
}

function requireActor(): MockCurrentUser {
  const actor = getMockCurrentUser();
  if (!actor) throw new ApiError(401, 'Not authenticated.');
  return actor;
}

function copy(run: PayrollReviewRun): PayrollReviewRun {
  return { ...run };
}

function refusal(message: string): ApiError {
  // The real API answers a ValidationException with 400 and a user-safe `detail`.
  return new ApiError(400, message);
}

export function listMockPayrollRuns(): PayrollReviewRun[] {
  // Newest first, matching the real endpoint's `ORDER BY CreatedAtUtc DESC`.
  return [...entries.values()].map((e) => copy(e.run)).sort((a, b) => b.createdAtUtc.localeCompare(a.createdAtUtc));
}

export function getMockPayrollRun(id: string): PayrollReviewRun {
  const entry = entries.get(id);
  if (!entry) throw new ApiError(404, 'Not Found');
  return copy(entry.run);
}

/** EPN-22: the mock has no calculation snapshots, so a run's attached calculations carry no tax warnings. Mirrors the real endpoint's 404 for an unknown run. */
export function listMockPayrollRunCalculations(id: string): PayrollRunCalculation[] {
  if (!entries.get(id)) throw new ApiError(404, 'Not Found');
  return [];
}

/** The live status Custom Pay Run displays for a handed-off run, or null if the id is unknown to the store. */
export function getMockPayrollRunStatus(id: string): string | null {
  return entries.get(id)?.run.status ?? null;
}

/** What `MockCustomPayRunGateway.submitForReview` does in the real flow: Module 7 gets a run created and
 * submitted by the signed-in user, with that user as the creator of its calculation(s). */
export function registerSubmittedMockRun(input: {
  id: string; periodStartDate: string; periodEndDate: string; checkDate: string; user: Actor;
}): PayrollReviewRun {
  const now = new Date().toISOString();
  const run = seedRun(input.id, [input.periodStartDate, input.periodEndDate, input.checkDate], PayrollRunStatus.PendingApproval,
    input.user, input.user, now);
  entries.set(run.id, { run, calculationCreatedByUserIds: [input.user.userId] });
  return copy(run);
}

export function approveMockRun(id: string): PayrollReviewRun {
  const actor = requireActor();
  const entry = entries.get(id);
  if (!entry) throw new ApiError(404, 'Not Found');
  const { run } = entry;
  if (run.status !== PayrollRunStatus.PendingApproval) throw refusal('Only a payroll run pending approval can be approved.');
  // The same three segregation-of-duties rules, in the same order, as PayrollRunService.ApproveAsync.
  if (actor.userId === run.createdByUserId) throw refusal('You cannot approve a payroll run you created yourself.');
  if (run.submittedByUserId && actor.userId === run.submittedByUserId) throw refusal('You cannot approve a payroll run you submitted yourself.');
  if (entry.calculationCreatedByUserIds.includes(actor.userId)) {
    throw refusal('You cannot approve a payroll run containing a calculation you ran yourself.');
  }
  run.status = PayrollRunStatus.Approved;
  run.approvedByUserId = actor.userId;
  run.approvedByEmailSnapshot = actor.email;
  run.approvedAtUtc = new Date().toISOString();
  return copy(run);
}

export function rejectMockRun(id: string, reason: string): PayrollReviewRun {
  const actor = requireActor();
  const entry = entries.get(id);
  if (!entry) throw new ApiError(404, 'Not Found');
  const { run } = entry;
  if (run.status !== PayrollRunStatus.PendingApproval) throw refusal('Only a payroll run pending approval can be rejected.');
  if (!reason || reason.trim().length === 0) throw refusal('A reason is required to reject a payroll run.');
  // EPN-241: the creator or submitter cannot reject their own run either (they withdraw it instead), same order as PayrollRunService.RejectAsync.
  if (actor.userId === run.createdByUserId) throw refusal('You cannot reject a payroll run you created yourself. Withdraw it instead.');
  if (run.submittedByUserId && actor.userId === run.submittedByUserId) throw refusal('You cannot reject a payroll run you submitted yourself. Withdraw it instead.');
  run.status = PayrollRunStatus.Rejected;
  run.rejectedByUserId = actor.userId;
  run.rejectedByEmailSnapshot = actor.email;
  run.rejectedAtUtc = new Date().toISOString();
  run.rejectionReason = reason;
  return copy(run);
}

/** EPN-241: what `PayrollRunService.WithdrawAsync` does. Only the run's own creator or submitter, only while pending, a reason is required. */
export function withdrawMockRun(id: string, reason: string): PayrollReviewRun {
  const actor = requireActor();
  const entry = entries.get(id);
  if (!entry) throw new ApiError(404, 'Not Found');
  const { run } = entry;
  if (run.status === PayrollRunStatus.Withdrawn && (actor.userId === run.createdByUserId || actor.userId === run.submittedByUserId)) return copy(run);
  if (run.status !== PayrollRunStatus.PendingApproval) {
    throw refusal(
      run.status === PayrollRunStatus.Approved
        ? 'An approved payroll run can no longer be withdrawn. Corrections after approval go through a paystub correction or reversal.'
        : 'Only a payroll run that is still pending approval can be withdrawn.',
    );
  }
  if (actor.userId !== run.createdByUserId && actor.userId !== run.submittedByUserId) {
    throw refusal('Only the person who created or submitted a payroll run can withdraw it. Anyone else rejects it instead.');
  }
  if (!reason || reason.trim().length === 0) throw refusal('A reason is required to withdraw a payroll run.');
  run.status = PayrollRunStatus.Withdrawn;
  run.rejectedByUserId = actor.userId;
  run.rejectedByEmailSnapshot = actor.email;
  run.rejectedAtUtc = new Date().toISOString();
  run.rejectionReason = reason.trim();
  return copy(run);
}
